import React, {FunctionComponent, useState, useMemo, ReactElement} from 'react';
import {Menu, MenuItem, Position} from '@blueprintjs/core';
import {Popover2} from "@blueprintjs/popover2";
import {IconNames} from '@blueprintjs/icons';

import {IFilter} from '../../../models';
import ToolbarButton from "../../toolbar/ToolbarButton";
import ListFilterItemComponent from './ListFilterItem';
import {isGuest, isLoggedIn} from '../../../utils/auth';
import {useCurrentUser} from "../../../hooks/use-user-current-workspace";

import './ListFilters.scss';

type SaveViewCallback = (filters: IFilter[]) => void;
type AddFilterCallback = (filter: IFilter) => void;
type UpdateFilterCallback = (filter: IFilter, index: number) => void;
type RemoveFilterCallback = (filter: IFilter) => void;

type ListFiltersComponentProps = {
  selectedFilters?: IFilter[];
  allFilters?: IFilter[];
  disableViews?: boolean;
  hideViews: boolean;
  disableFilter?: boolean;
  leftToolbarContent?: ReactElement;
  onSaveView?: SaveViewCallback;
  onAddFilter?: AddFilterCallback;
  onUpdateFilter?: UpdateFilterCallback;
  onRemoveFilter?: RemoveFilterCallback;
};

type FilterMenuContentProps = {
  allFilters?: IFilter[];
  onAddFilterToConfigure: (filter: IFilter) => void
  selectedFilters: IFilter[];
}

const filterNames: { [key: string]: string } = {
  folder_id: 'Folder',
  tags: 'Tags',
  name: 'Name',
  created_by: 'Created by',
  created_on: 'Created on',
  changed_by: 'Modified by',
  changed_on: 'Modified on'
}

const FilterMenuContent = ({allFilters, onAddFilterToConfigure, selectedFilters}: FilterMenuContentProps) => {
  const sortedFormattedFilters = useMemo(() => {
    const filterKeys = Object.keys(filterNames)
    const formattedFilters = allFilters?.filter(f => filterKeys.includes(f.name))
      .map(f => ({...f, label: filterNames[f.name], disabled: !!selectedFilters.find(sf => sf.name === f.name)}))
    return formattedFilters?.sort((a, b) => filterKeys.indexOf(a.name) - filterKeys.indexOf(b.name)) || [];
  }, [allFilters])

  return (
    <Menu data-cy='filterItems'>
      {
        sortedFormattedFilters.map((filter) => (
          <MenuItem
            key={`${filter.name}-${filter.opr}-${filter.value}`}
            text={filter.label}
            onClick={() => onAddFilterToConfigure(filter)}
            disabled={filter.disabled}
          />
        ))
      }
    </Menu>
  )
}

export const ListFiltersComponent: FunctionComponent<ListFiltersComponentProps> = ({
  selectedFilters,
  allFilters,
  disableViews,
  hideViews,
  disableFilter,
  leftToolbarContent,
  onSaveView,
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter
}: ListFiltersComponentProps) => {
  const [filtersToConfigure, setFiltersToConfigure] = useState<IFilter[]>([]);
  const currentUser = useCurrentUser();

  const onSaveViewClick = () => {
    let filters = [] as IFilter[];
    if (selectedFilters) {
      filters = filters.concat(selectedFilters);
    }
    onSaveView && onSaveView(filters);
  };

  const onAddFilterToConfigure = (filter: IFilter) => {
    setFiltersToConfigure(filtersToConfigure.concat(filter));
  };

  const onRemoveConfiguredFilter = (filter: IFilter) => {
    onRemoveFilter && onRemoveFilter(filter);
  };

  const onRemoveFilterToConfigure = (filter: IFilter) => {
    setFiltersToConfigure(prev => prev.filter(f => f.name !== filter.name));
  };

  const onApplyFilterToConfigure = (filter: IFilter) => {
    onRemoveFilterToConfigure(filter);
    onAddFilter && onAddFilter(filter);
  };

  const onApplyFilterToUpdate = (filter: IFilter, index: number) => {
    onUpdateFilter && onUpdateFilter(filter, index);
  };

  const isNotGuest = useMemo(() => isLoggedIn(currentUser) && !isGuest(currentUser), [currentUser]);
  return (
    <div className="list__tags">
      {
        leftToolbarContent && (
          <div className="list__tags__left-toolbar">
            {leftToolbarContent}
          </div>
        )
      }
      <div className="list__tags__list">
        {
          selectedFilters && selectedFilters.map((filter, index) => (
            <ListFilterItemComponent
              index={index}
              key={filter.name}
              filter={filter}
              configured={true}
              onApply={onApplyFilterToUpdate}
              onRemove={() => onRemoveConfiguredFilter(filter)}
            />
          ))
        }
        {
          filtersToConfigure && filtersToConfigure.map((filter, index) => (
            <ListFilterItemComponent
              index={index}
              key={filter.name}
              filter={filter}
              configured={false}
              onApply={onApplyFilterToConfigure}
              onRemove={() => onRemoveFilterToConfigure(filter)}
            />
          ))
        }
      </div>
      <div className="list__tags__toolbar" data-cy='filterListTagsToolbar'>
        {
          !disableViews && isNotGuest && selectedFilters && selectedFilters?.length > 0 && (
            <ToolbarButton
              icon={IconNames.BOOKMARK}
              label="Save View"
              onClick={onSaveViewClick}
            />
          )
        }
        {
          !disableFilter &&
          <Popover2
            content={
              <FilterMenuContent
                allFilters={allFilters}
                onAddFilterToConfigure={onAddFilterToConfigure}
                selectedFilters={[...filtersToConfigure, ...(selectedFilters || [])]}
              />
            }
            position={Position.BOTTOM_RIGHT}
            className="list__tags__toolbar__trigger"
          >
            <ToolbarButton icon={IconNames.FILTER_KEEP} label="Add Filter"></ToolbarButton>
          </Popover2>
        }
      </div>
    </div>
  );
}
